/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import styled from "styled-components";
import { Button, Dropdown, Input, Spacer, Text, theme } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { validateLink } from "~/components/BasicCustomerInfo/validation";
import { StyledErrorText } from "~/components/Contract/ContractDurationDates";
import { Divider } from "~/components/Styled/Divider";
import { Link } from "../types";

type LinkItemProps = {
  link: Link;
  updateLink: (link: Link) => void;
  deleteLink: (id: string) => void;
};

const StyledErrorTextInline = styled(Text)`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.color.text.text02};
  margin-right: 0.5rem;
`;

const StyledButtonTextInline = styled(Text)`
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.color.text.text02};
  text-decoration: underline;
`;

const ESCALATION_PAGE_INCLUDE_DEFAULT =
  "https://nordcloud.atlassian.net/wiki/spaces/MC/pages/3139798002";

const DROPDOWN_LABELS = [
  "Jira Customer Portal",
  "Jira Project",
  "Jira Premium Support",
  "Confluence Space",
  "Management Escalation Page Include",
  "Nordcloud Contact Information Page Include",
  "AutoPatcher Link",
  "AutoBackup Link",
  "RACI Matrix",
  "Managed Cloud Responsibilities",
  "Customer Onboarding Ticket",
  "Environement Services",
  "Customer Major Incident Definition",
  "Customer Major Incident Management Escalation Process",
  "Runbook Repository",
  "Other",
];
const DROPDOWN_OPTIONS = DROPDOWN_LABELS.map((label) => ({
  label,
  value: label,
}));

function NamedLinkItem({ link, updateLink, deleteLink }: LinkItemProps) {
  const [nameError, setNameError] = React.useState(false);
  const [urlError, setUrlError] = React.useState(false);

  const handleChange = (value: string, field: "name" | "desc" | "url") => {
    const updated = { ...link, [field]: value };
    const validationErrors = validateLink(updated);

    setNameError(validationErrors.includes("name"));
    setUrlError(validationErrors.includes("url"));

    updateLink(updated);
  };

  return (
    <>
      <Divider>
        <FlexContainer>
          <FlexContainer direction="column" align="flex-start" grow>
            <Input
              type="text"
              data-cy="link-name-text"
              small
              defaultValue={link.name}
              placeholder="Name"
              onChange={(e) => handleChange(e.target.value, "name")}
            />
            {nameError && <StyledErrorText>Name is required</StyledErrorText>}

            <Spacer height="0.5rem" />
            <Dropdown
              onChange={(e) => handleChange(e, "desc")}
              value={link.desc}
              css={{ fontSize: "0.75rem" }}
              options={DROPDOWN_OPTIONS}
              name="Choose Type"
            />

            <Spacer height="0.5rem" />
            <Input
              type="text"
              data-cy="link-url-text"
              small
              value={link.url}
              placeholder="Url"
              onChange={(e) => handleChange(e.target.value, "url")}
            />
            <FlexContainer>
              {urlError && (
                <StyledErrorTextInline>Invalid Link</StyledErrorTextInline>
              )}
              {link.desc === "Management Escalation Page Include" &&
                link.url !== ESCALATION_PAGE_INCLUDE_DEFAULT && (
                  <StyledButtonTextInline
                    onClick={() => {
                      handleChange(ESCALATION_PAGE_INCLUDE_DEFAULT, "url");
                    }}
                  >
                    (Use Default Escalation Page Value)
                  </StyledButtonTextInline>
                )}
            </FlexContainer>
          </FlexContainer>

          <Button
            icon="minus"
            type="button"
            data-cy="link-delete-link"
            css={{ marginLeft: "0.5rem" }}
            severity="low"
            onClick={() => deleteLink(link.id)}
          />
        </FlexContainer>
        <Spacer height="1rem" />
      </Divider>
    </>
  );
}

export { NamedLinkItem };
